import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Camera, X, Check } from 'lucide-react';
import Button from '../shared/Button';
import Input from '../shared/Input';
import SearchSelect from '../shared/SearchSelect';
import ImageBrowser from '../shared/ImageBrowser';
import ModalShell from '../shared/ModalShell';

/** Same bucket as Photo Gallery / max_images — excludes portfolio uploads */
const NON_PORTFOLIO_IMAGE_QUERY = { non_portfolio: true };

const ServiceModal = ({
  isOpen,
  isEdit,
  formData,
  categories,
  specialties = [],
  onClose,
  onSubmit,
  onInputChange,
  isLoading,
  onImageSelect,
  maxImages = null
}) => {
  const [showImageBrowser, setShowImageBrowser] = React.useState(false);

  const selectedTags = useMemo(() => {
    if (!formData.search_tags) return [];
    return formData.search_tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }, [formData.search_tags]);

  const toggleSearchTag = (specialty) => {
    const next = selectedTags.includes(specialty)
      ? selectedTags.filter((tag) => tag !== specialty)
      : [...selectedTags, specialty];
    onInputChange('search_tags', next.join(', '));
  };

  const handleClose = () => {
    if (!isLoading && !showImageBrowser) onClose();
  };

  const handleImageSelect = (image) => {
    if (image && onImageSelect) {
      onImageSelect(image);
      setShowImageBrowser(false);
    }
  };

  return (
      <ModalShell
        isOpen={isOpen}
        onClose={handleClose}
        labelledBy="service-modal-title"
        panelClassName="max-w-2xl"
      >
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 max-h-[min(90vh,900px)] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-900">
            <h3 id="service-modal-title" className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {isEdit ? 'Edit Service' : 'Create New Service'}
            </h3>
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 !rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={onSubmit} className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Service Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.service_name}
                    onChange={(e) => onInputChange('service_name', e.target.value)}
                    placeholder="Enter service name"
                    required
                  />
                </div>
                
                {/* Service Image */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Service Image
                  </label>
                  <div className="flex items-center gap-4">
                    {formData.image_url ? (
                      <div className="relative">
                        <img
                          src={formData.image_url}
                          alt="Service"
                          className="w-24 h-24 rounded-xl object-cover border-2 border-gray-200 dark:border-gray-700"
                        />
                        <button
                          type="button"
                          onClick={() => onInputChange('image_url', '')}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-gray-200 dark:bg-gray-800 flex items-center justify-center border-2 border-gray-300 dark:border-gray-600">
                        <Camera className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <Button
                      type="button"
                      onClick={() => setShowImageBrowser(true)}
                      variant="outline"
                      size="sm"
                      icon={Camera}
                      className="!rounded-xl"
                    >
                      {formData.image_url ? 'Change Image' : 'Select Image'}
                    </Button>
                  </div>
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category *
                  </label>
                  <SearchSelect
                    value={formData.service_category_id}
                    onChange={(value) => onInputChange('service_category_id', value)}
                    options={categories.map(category => ({
                      value: category.id,
                      label: category.category_name
                    }))}
                    placeholder="Select a category"
                    required
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => onInputChange('description', e.target.value)}
                    placeholder="Describe your service in detail"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                    required
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Search Tags
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Select from your business specialties to help customers find this service.
                  </p>

                  {specialties.length > 0 ? (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {specialties.map((specialty) => {
                          const isSelected = selectedTags.includes(specialty);
                          return (
                            <button
                              key={specialty}
                              type="button"
                              onClick={() => toggleSearchTag(specialty)}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                                isSelected
                                  ? 'bg-primary-100 text-primary-800 border border-primary-300 ring-1 ring-primary-200 dark:bg-primary-950/40 dark:text-primary-200 dark:border-primary-800'
                                  : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700'
                              }`}
                              aria-pressed={isSelected}
                            >
                              {isSelected && <Check className="w-3.5 h-3.5" />}
                              {specialty}
                            </button>
                          );
                        })}
                      </div>
                      {selectedTags.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          {selectedTags.length} of {specialties.length} selected
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-950/50 p-4 text-sm text-gray-600 dark:text-gray-400">
                      <p className="mb-2">You have not added any specialties to your business profile yet.</p>
                      <Link
                        to="/vendor/account?tab=business"
                        className="text-primary-600 hover:text-primary-700 dark:text-primary-400 underline text-sm"
                        onClick={onClose}
                      >
                        Add specialties in Business Profile
                      </Link>
                    </div>
                  )}
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Starting Price ($) *
                  </label>
                  <Input
                    type="number"
                    value={formData.base_price}
                    onChange={(e) => onInputChange('base_price', e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    hideSpinner={true}
                    preventTextInput={true}
                    formatNumber={true}
                    decimalPlaces={2}
                    required
                  />
                </div>
                
                <div className="sm:col-span-2 hidden">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => onInputChange('is_featured', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Featured Service</span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Featured services appear prominently in search results
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-800">
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="outline"
                  className="w-full sm:w-auto !rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  loading={isLoading}
                  className="w-full sm:w-auto !rounded-xl"
                >
                  {isEdit ? 'Update Service' : 'Create Service'}
                </Button>
              </div>
          </form>

          {/* Nested image browser overlay inside panel */}
          {showImageBrowser && (
            <div className="absolute inset-0 z-20 flex flex-col bg-white dark:bg-gray-900 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Select Service Image</h3>
                <button
                  type="button"
                  onClick={() => setShowImageBrowser(false)}
                  className="p-2 !rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200"
                  aria-label="Close image browser"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 p-4 sm:p-6">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Shows <strong>general</strong> uploads only (portfolio images are on the Portfolio page). Limits follow your plan&apos;s{' '}
                  <strong>max uploaded images</strong> cap.
                  {maxImages === null ? ' Current plan: unlimited.' : ` Current plan: up to ${maxImages} general images.`}
                </p>
                <ImageBrowser
                  listQueryParams={NON_PORTFOLIO_IMAGE_QUERY}
                  uploadImageType="general"
                  showUpload
                  showDelete
                  showSetDefault={false}
                  maxImages={maxImages}
                  onSelectImage={handleImageSelect}
                />
              </div>
            </div>
          )}
        </div>
      </ModalShell>
  );
};

export default ServiceModal;
